import { z } from 'zod';

/**
 * The one shape a task authoring surface works in — the portals' dialog and side panel, and the
 * extension's create/edit route.
 *
 * `status` is a plain string, not an enum: task statuses are workspace-configurable rows, so the
 * valid set is data, not a compile-time union. The extension previously used a fixed
 * `['todo','in-progress','done']` enum here, which meant a workspace with any other status could
 * not select it.
 *
 * Due date and time are separate fields because the pickers are separate controls; they are
 * combined into a single epoch-seconds `dueAt` at submit. `dueEnabled` / `reminderEnabled` /
 * `endRepeatEnabled` are UI toggles, not stored values — a relative reminder has nothing to
 * anchor to without a due date, so the form models that dependency explicitly.
 */
/**
 * Mirrors `taskFrequency` in `backend/src/db/schema.ts`. Unlike a task's *status* — which is
 * workspace-configurable data and therefore a plain string — a repeat frequency is a fixed set
 * the backend column enumerates, so it stays a compile-time union.
 */
export const TASK_REPEAT_FREQUENCIES = [
  'never',
  'hourly',
  'daily',
  'weekly',
  'biweekly',
  'monthly',
  'yearly',
] as const;

export type TaskRepeatFrequency = (typeof TASK_REPEAT_FREQUENCIES)[number];

export const taskFormSchema = z.object({
  title: z.string().min(1, 'Task title is required'),
  description: z.string(),
  /** A workspace `taskStatuses` key. */
  status: z.string(),
  assignedTo: z.number().nullable().optional(),
  priority: z.number(),
  /** Related contact, or null when standalone. */
  contactId: z.number().nullable().optional(),
  /** The page this task belongs to. Skill Viper tasks hang off an anchor, not a contact. */
  anchorId: z.number().nullable().optional(),

  dueEnabled: z.boolean(),
  dueDate: z.date().nullable().optional(),
  /** `HH:mm`, empty when no time of day was chosen. */
  dueTime: z.string(),

  reminderEnabled: z.boolean(),
  remindBeforeMinutes: z.number(),

  repeatFrequency: z.enum(TASK_REPEAT_FREQUENCIES),
  endRepeatEnabled: z.boolean(),
  endRepeatDate: z.date().nullable().optional(),
});

export type TaskFormValues = z.infer<typeof taskFormSchema>;

const REPEAT_LABELS: Record<TaskRepeatFrequency, string> = {
  never: 'Never',
  hourly: 'Hourly',
  daily: 'Daily',
  weekly: 'Weekly',
  biweekly: 'Bi-weekly',
  monthly: 'Monthly',
  yearly: 'Yearly',
};

export const REPEAT_OPTIONS = TASK_REPEAT_FREQUENCIES.map((value) => ({
  value,
  label: REPEAT_LABELS[value],
}));

export const REMIND_BEFORE_OPTIONS = [
  { value: 0, label: 'At time of event' },
  { value: 5, label: '5 minutes before' },
  { value: 10, label: '10 minutes before' },
  { value: 15, label: '15 minutes before' },
  { value: 30, label: '30 minutes before' },
  { value: 60, label: '1 hour before' },
  { value: 120, label: '2 hours before' },
  { value: 1440, label: '1 day before' },
] as const;

/** Blank form state. Callers override what they know; `status` needs the workspace's default. */
export function emptyTaskForm(defaultStatus: string): TaskFormValues {
  return {
    title: '',
    description: '',
    status: defaultStatus,
    assignedTo: null,
    priority: 0,
    contactId: null,
    anchorId: null,
    dueEnabled: false,
    dueDate: null,
    dueTime: '',
    reminderEnabled: false,
    remindBeforeMinutes: 15,
    repeatFrequency: 'never',
    endRepeatEnabled: false,
    endRepeatDate: null,
  };
}

/**
 * Folds the form's separate date and time controls into the single epoch-seconds `dueAt` the API
 * stores. Returns null when the due toggle is off or no date was picked.
 */
export function toDueAtSeconds(values: {
  dueEnabled: boolean;
  dueDate?: Date | null;
  dueTime: string;
}): number | null {
  if (!values.dueEnabled || !values.dueDate) return null;

  const due = new Date(values.dueDate);
  const [hours, minutes] = values.dueTime.split(':').map(Number);
  if (Number.isFinite(hours) && Number.isFinite(minutes)) {
    due.setHours(hours, minutes, 0, 0);
  } else {
    due.setHours(0, 0, 0, 0);
  }
  return Math.floor(due.getTime() / 1000);
}

/** Splits a stored `dueAt` back into the form's date and time controls. */
export function fromDueAtSeconds(dueAt: number | null | undefined): {
  dueEnabled: boolean;
  dueDate: Date | null;
  dueTime: string;
} {
  if (!dueAt) return { dueEnabled: false, dueDate: null, dueTime: '' };

  const date = new Date(dueAt * 1000);
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const hasTimeOfDay = date.getHours() !== 0 || date.getMinutes() !== 0;

  return {
    dueEnabled: true,
    dueDate: date,
    dueTime: hasTimeOfDay ? `${hours}:${minutes}` : '',
  };
}

/**
 * Maps the form's values into the shape `tasks.create` accepts.
 *
 * The date/time folding and the reminder offset live here so every authoring surface computes
 * them identically — each caller previously did its own arithmetic, which is how the portals and
 * the extension ended up storing different things for the same input.
 */
export function toCreateTaskInput(values: TaskFormValues) {
  const dueAt = toDueAtSeconds(values);
  const remindBeforeMinutes =
    dueAt !== null && values.reminderEnabled ? values.remindBeforeMinutes : null;

  return {
    label: values.title,
    content: values.description,
    status: values.status || undefined,
    priority: values.priority,
    assignedTo: values.assignedTo ?? null,
    contactId: values.contactId ?? undefined,
    anchorId: values.anchorId ?? undefined,
    dueAt,
    remindBeforeMinutes,
    remindAt:
      dueAt !== null && remindBeforeMinutes !== null ? dueAt - remindBeforeMinutes * 60 : null,
    reminderFreq: values.repeatFrequency,
    endRepeatAt:
      values.repeatFrequency !== 'never' && values.endRepeatEnabled && values.endRepeatDate
        ? Math.floor(values.endRepeatDate.getTime() / 1000)
        : null,
  };
}
